import { zodResolver } from '@hookform/resolvers/zod'
import {
    Droplets,
    Flower2,
    Leaf,
    MoreHorizontal,
    Scissors,
    SprayCan,
    Sprout,
    Trees,
    Wheat,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useCareLogWizard } from './-care-log-wizard'
import type { LucideIcon } from 'lucide-react'
import type { Dispatch, SetStateAction } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import type {
    CareLogActivityType,
    MyGrower,
    UpsertCareLogRequest,
} from '@/lib/api/care-logs-api'
import type {
    CareLogFormInput,
    CareLogFormValues,
} from '@/lib/schemas/care-log-schemas'
import type { CareLogWizard } from './-care-log-wizard'
import type { MultiSelectOption } from './-care-log-multi-select'
import { useAgriculturalMaterial } from '@/hooks/use-agricultural-materials-query'
import { useAuth } from '@/hooks/use-auth'
import { useSelectableUsers } from '@/hooks/use-user-query'
import {
    useCareLog,
    useCareLogAreaContext,
    useCareLogGrowerAreas,
    useCreateCareLog,
    useMyGrowers,
    useUpdateCareLog,
} from '@/hooks/use-care-logs-query'
import { m } from '@/lib/paraglide/messages'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { createCareLogSchema } from '@/lib/schemas/care-log-schemas'

export interface CareLogEditProps {
    mode: 'create' | 'edit'
    careLogId?: string
    /**
     * Pre-selected activity for a new log (create mode only) — e.g. when opened
     * from a care-reminder push deep link. Ignored in edit mode.
     */
    initialActivity?: CareLogActivityType
    /**
     * Vùng trồng của mã QR vừa quét (create mode only). Server xác nhận tài
     * khoản được ghi cho hộ sở hữu vùng → điền sẵn bước Địa điểm (hộ + vùng) và
     * tự sang bước Công việc. Server từ chối thì form như tạo mới bình thường.
     */
    initialAreaId?: string
    /** Called after a successful save (route navigates back to the list). */
    onSaved: () => void
    /** Back-arrow handler (route navigates back to the list). */
    onBack: () => void
}

// Đúng 8 loại như hệ cũ, theo thứ tự BP·PT·TN·LC·CC·NN·HC·K
// (không có "Trồng dặm"/replanting). Giá trị replanting vẫn còn trong
// CareLogActivityType để hiển thị bản ghi cũ (nếu có), nhưng không cho chọn mới.
export const ACTIVITY_TYPES: CareLogActivityType[] = [
    'fertilizing',
    'spraying',
    'watering',
    'weeding',
    'pruning',
    'bud_picking',
    'harvesting',
    'other',
]

export const activityLabel: Record<CareLogActivityType, () => string> = {
    fertilizing: m.care_log_activity_fertilizing,
    harvesting: m.care_log_activity_harvesting,
    replanting: m.care_log_activity_replanting,
    spraying: m.care_log_activity_spraying,
    weeding: m.care_log_activity_weeding,
    watering: m.care_log_activity_watering,
    pruning: m.care_log_activity_pruning,
    bud_picking: m.care_log_activity_bud_picking,
    other: m.care_log_activity_other,
}

/** Icon fallback cho loại hoạt động chưa có ảnh (vd Trồng dặm). */
export const activityIcon: Record<CareLogActivityType, LucideIcon> = {
    fertilizing: Sprout,
    spraying: SprayCan,
    harvesting: Leaf,
    weeding: Wheat,
    watering: Droplets,
    pruning: Scissors,
    replanting: Trees,
    bud_picking: Flower2,
    other: MoreHorizontal,
}

/** Ảnh trực quan cho từng loại hoạt động — copy từ hệ cũ (`/images/actions`),
 *  đã resize/nén. Loại không có ảnh (replanting) sẽ dùng icon fallback ở trên. */
export const activityImage: Partial<Record<CareLogActivityType, string>> = {
    fertilizing: '/images/actions/bon_phan.jpg',
    spraying: '/images/actions/phun_thuoc.jpg',
    watering: '/images/actions/tuoi_nuoc.jpg',
    weeding: '/images/actions/lam_co.jpg',
    pruning: '/images/actions/cup_che.jpg',
    bud_picking: '/images/actions/nhat_nu.jpg',
    harvesting: '/images/actions/hai_che.jpg',
    other: '/images/actions/cong_viec_khac.png',
}

/** Local YYYY-MM-DD (not UTC) so "today" matches the user's calendar day. */
export function localDateStr(offsetDays = 0): string {
    const d = new Date()
    d.setDate(d.getDate() + offsetDays)
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const da = String(d.getDate()).padStart(2, '0')
    return `${d.getFullYear()}-${mo}-${da}`
}

/** Local YYYY-MM-DDTHH:mm cho input datetime-local (ngày + giờ hiện tại + offset
 *  ngày). Wall-clock địa phương, khớp cách hệ cũ lưu ngày giờ thực hiện. */
export function localDateTimeStr(offsetDays = 0): string {
    const d = new Date()
    d.setDate(d.getDate() + offsetDays)
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const da = String(d.getDate()).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    return `${d.getFullYear()}-${mo}-${da}T${hh}:${mi}`
}

/** Everything both the mobile and desktop layouts of the care-log form need.
 *  Built once in the container so the two layouts stay pure presentation. */
export interface CareLogEditContext {
    mode: 'create' | 'edit'
    form: UseFormReturn<CareLogFormInput, object, CareLogFormValues>
    onBack: () => void
    loadingInitial: boolean
    /** Mở từ mã QR và đang chờ ngữ cảnh QR + danh sách vùng — chưa hiện form. */
    prefillingFromQr: boolean
    submitting: boolean
    isAdmin: boolean
    growersLoading: boolean
    households: MyGrower[]
    noHousehold: boolean
    initialGrowerName?: string
    selectedGrowerId: string
    handleGrowerChange: (growerId: string) => void
    /** Người thực hiện đang chọn + option (mọi user active). */
    selectedImplementerIds: string[]
    implementerOptions: MultiSelectOption[]
    activity: CareLogActivityType
    logDate: string
    selectedParcels: string[]
    needsMaterials: boolean
    needsQuantity: boolean
    needsQuarantine: boolean
    /** Label động + gợi ý quy đổi cho ô định lượng (theo hoạt động). */
    quantityLabel: string
    quantityHint: string
    /** Label ô vật tư theo hoạt động (Phân bón / Thuốc bảo vệ thực vật). */
    materialsLabel: string
    materialFallbackLabels: Record<
        string,
        { name: string; image?: string | null; unit?: string | null }
    >
    parcelOptionsWithOrphans: Array<{ value: string; label: string }>
    /** Hộ đang chọn có vùng trồng → phải chọn ít nhất 1 (đánh dấu * trên nhãn). */
    requireParcels: boolean
    /** Đang tải danh sách vùng trồng của hộ vừa chọn. */
    loadingParcels: boolean
    /** Evidence file previews (id → url/kind). Lives in the container so it
     *  survives the mobile⇄desktop layout swap (which unmounts the layout). */
    filePreviews: Record<string, FilePreview>
    setFilePreviews: Dispatch<SetStateAction<Record<string, FilePreview>>>
    /** Ảnh/video minh chứng đang upload dở. Nút Lưu phải chờ: `file_ids` chỉ
     *  được ghi sau khi từng file upload xong, lưu sớm là mất ảnh không báo. */
    uploadingFiles: boolean
    setUploadingFiles: Dispatch<SetStateAction<boolean>>
    handleSubmit: (values: CareLogFormValues) => Promise<void>
    toggleParcel: (id: string) => void
    /** Điều hướng wizard 3 bước (Địa điểm · Công việc · Xác minh). */
    wizard: CareLogWizard
}

/** Preview of one uploaded evidence file. */
export interface FilePreview {
    url: string
    isVideo: boolean
}

/**
 * Shared state + handlers for the care-log create/edit form. The mobile and
 * desktop layouts differ structurally (button grid vs dropdown, fixed footer vs
 * inline), so they are separate components — but they run identical logic, which
 * lives here so there is a single source of truth for validation and submit.
 */
/**
 * Có bắt buộc chọn vùng trồng không.
 *
 * Hộ CÓ vùng → bắt buộc (nhật ký phải truy được làm ở đâu). Hộ chưa khai vùng
 * nào → bỏ trống được, vì không có gì để chọn; phần lớn hộ trên hệ thống đang ở
 * trạng thái này, chặn cứng là khoá luôn nghiệp vụ của họ.
 *
 * ĐANG TẢI cũng tính là bắt buộc: lúc đó danh sách vùng còn rỗng nên không phân
 * biệt được "hộ chưa khai vùng" với "chưa biết". Nới sớm thì bấm "Tiếp theo"
 * trong vài trăm ms đầu sẽ lọt qua bước Địa điểm, tới bước cuối bấm Lưu mới bị
 * đá ngược về — đúng thứ wizard sinh ra để tránh. Chặn nhầm một nhịp rồi tự nới
 * khi query xong thì vô hại.
 */
export function shouldRequireParcels(
    loadingAreas: boolean,
    parcelOptionCount: number,
): boolean {
    return loadingAreas || parcelOptionCount > 0
}

export function useCareLogEditContext({
    mode,
    careLogId,
    initialActivity,
    initialAreaId,
    onSaved,
    onBack,
}: CareLogEditProps): CareLogEditContext {
    const { data: qrArea, isLoading: loadingQrArea } = useCareLogAreaContext(
        mode === 'create' ? (initialAreaId ?? null) : null,
    )
    const { data: initial, isLoading: loadingInitial } = useCareLog(
        mode === 'edit' ? (careLogId ?? null) : null,
    )
    const workflow = useWorkflowTracking('care_log', {
        active: mode === 'create' || !loadingInitial,
        attemptKey: careLogId,
        operation: mode === 'edit' ? 'update' : 'create',
    })
    const createMut = useCreateCareLog()
    const updateMut = useUpdateCareLog(careLogId ?? '')
    const submitting = createMut.isPending || updateMut.isPending

    const { hasPermission, user } = useAuth()
    // Người thực hiện mặc định = tài khoản đang đăng nhập (giống hệ cũ), trừ khi
    // đang sửa một nhật ký đã có danh sách implementer riêng.
    const defaultImplementers = user?.id ? [user.id] : []

    // Giờ mở form, CHỐT MỘT LẦN. Trước đây `values.log_date` gọi thẳng
    // `localDateTimeStr(0)` trong thân render: chuỗi đó đổi mỗi khi sang phút
    // mới, mà RHF deep-compare `values` rồi `_reset` khi thấy khác → cứ qua phút
    // là toàn bộ dữ liệu hộ vừa nhập (vật tư, cách ly, vùng trồng, ảnh) bị xoá
    // trắng, và ở bước cuối thì bấm "Lưu" chỉ trượt validate trong im lặng.
    const openedAtRef = useRef(localDateTimeStr(0))

    // Hộ có khai vùng trồng hay không chỉ biết được SAU khi query vùng trả về,
    // mà resolver thì chốt ngay lúc useForm. Đọc qua ref để mỗi lần validate
    // lấy giá trị mới nhất, khỏi phải dựng lại form (dựng lại = mất dữ liệu đang nhập).
    const requireParcelsRef = useRef(false)

    const form = useForm<CareLogFormInput, object, CareLogFormValues>({
        resolver: (values, context, opts) =>
            zodResolver(
                createCareLogSchema({
                    requireParcels: requireParcelsRef.current,
                }),
            )(values, context, opts),
        // `values` (not defaultValues) so the form re-syncs once the edited log
        // loads. RHF deep-compares, so the freshly-mapped arrays don't loop.
        // MỌI field ở đây phải ổn định giữa các render — bất kỳ giá trị nào tự
        // đổi theo thời gian sẽ reset sạch form.
        values: {
            // Mở từ mã QR: hộ + vùng lấy từ server (không tin URL) → bước Địa
            // điểm điền sẵn. RHF so sâu nên mảng parcels dựng lại mỗi render
            // không gây reset.
            grower_id: initial?.grower_id ?? qrArea?.grower_id ?? '',
            activity_type:
                initial?.activity_type ?? initialActivity ?? 'fertilizing',
            // datetime-local cần "YYYY-MM-DDTHH:mm" (16 ký tự đầu của ISO).
            log_date: initial?.log_date
                ? initial.log_date.slice(0, 16)
                : openedAtRef.current,
            quantity: initial?.quantity ?? 0,
            quarantine_note: initial?.quarantine_note ?? '',
            note: initial?.note ?? '',
            parcels: initial?.parcels ?? (qrArea ? [qrArea.area_id] : []),
            materials: (initial?.materials ?? []).map((mm) => ({
                agricultural_material_id: mm.agricultural_material_id,
                quantity: mm.quantity ?? 0,
                self_bought: mm.self_bought ?? false,
            })),
            file_ids: (initial?.files ?? []).map((f) => f.id),
            implementer_ids: initial?.implementer_ids ?? defaultImplementers,
        },
    })

    const isAdmin = hasPermission('care_logs.manage_all')
    const { data: myGrowers, isLoading: growersLoading } =
        useMyGrowers(!isAdmin)
    const households = myGrowers ?? []
    const noHousehold = !isAdmin && !growersLoading && households.length === 0
    const soleHouseholdId =
        !isAdmin && households.length === 1 ? households[0].id : ''
    useEffect(() => {
        if (soleHouseholdId && !form.getValues('grower_id')) {
            form.setValue('grower_id', soleHouseholdId, {
                shouldValidate: true,
            })
        }
    }, [soleHouseholdId, form])

    // Người thực hiện — danh sách mọi user active (endpoint auth-only, hộ dân
    // cũng gọi được). Label "Họ tên (username)" giống hệ cũ.
    const selectedGrowerId = form.watch('grower_id')

    const { data: selectableUsers } = useSelectableUsers()

    const activity = form.watch('activity_type')
    const logDate = form.watch('log_date')
    const selectedParcels = form.watch('parcels') ?? []
    const selectedImplementerIds = form.watch('implementer_ids') ?? []

    const activeImplementerOptions: MultiSelectOption[] = (
        selectableUsers ?? []
    ).map((u) => ({
        value: u.id,
        label: u.full_name ? `${u.full_name} (${u.username})` : u.username,
    }))
    // Giữ hiển thị (và cho bỏ chọn) những implementer đã lưu nhưng nay không còn
    // trong danh sách active (vd tài khoản bị vô hiệu hóa) — tránh mất ngầm khi
    // sửa nhật ký cũ. Giống cơ chế orphan của Vùng trồng.
    const knownImplementerIds = new Set(
        activeImplementerOptions.map((o) => o.value),
    )
    const implementerOptions: MultiSelectOption[] = [
        ...activeImplementerOptions,
        ...selectedImplementerIds
            .filter((id) => !knownImplementerIds.has(id))
            .map((id) => ({
                value: id,
                label: m.care_log_implementer_inactive(),
            })),
    ]

    // Which extra questions to ask depends on the activity (parity hệ cũ).
    const needsMaterials = activity === 'fertilizing' || activity === 'spraying'
    // Ô định lượng CHUNG giờ chỉ còn cho Hái chè (không có vật tư). Bón phân /
    // Phun thuốc nhập định lượng THEO TỪNG vật tư (xem CareLogMaterialsField) —
    // một ô chung không đủ để xuất kho khi phiếu dùng nhiều vật tư.
    const needsQuantity = activity === 'harvesting'
    // Cách ly: Bón phân + Phun thuốc.
    const needsQuarantine =
        activity === 'fertilizing' || activity === 'spraying'

    // Label + gợi ý quy đổi cho ô định lượng (giống hệ cũ, hardcode ÷25 / ÷1000).
    const quantityLabel =
        activity === 'harvesting'
            ? m.care_log_quantity_harvesting()
            : activity === 'spraying'
              ? m.care_log_quantity_spraying()
              : m.care_log_quantity_fertilizing()
    const quantityNum = Number(form.watch('quantity'))
    const quantityHint =
        quantityNum > 0 && activity === 'fertilizing'
            ? m.care_log_quantity_hint_bag({
                  count: Math.round(quantityNum / 25),
              })
            : quantityNum > 0 && activity === 'spraying'
              ? m.care_log_quantity_hint_liter({
                    liters: (quantityNum / 1000).toFixed(2),
                })
              : ''

    // Label ô vật tư theo hoạt động: Bón phân → "Phân bón", Phun thuốc → "Thuốc
    // bảo vệ thực vật" (giống hệ cũ tách phân/thuốc).
    const materialsLabel =
        activity === 'spraying'
            ? m.care_log_materials_pesticide()
            : activity === 'fertilizing'
              ? m.care_log_materials_fertilizer()
              : m.care_log_materials()

    // Auto-fill thời gian cách ly khi chọn đúng 1 vật tư → lấy
    // `quarantine_period` của vật tư đó. Áp dụng cho CẢ Bón phân lẫn Phun thuốc:
    // phân bón trong danh mục cũng có trường này, bắt hộ gõ tay trong khi số đã
    // nằm sẵn là vô lý (hệ cũ chỉ làm cho thuốc vì bên đó bón phân không có ô).
    // NÉ BUG đè dữ liệu khi mở sửa: chỉ auto-fill khi người dùng ĐỔI tập vật tư
    // so với lúc form load. `baselineRef` = tập vật tư "đã xử lý" gần nhất, ĐƯỢC
    // ARM từ `initial` (không phải từ watch) SAU khi nhật ký load xong → tránh
    // race lúc edit-load async.
    const materialIdsKey = (form.watch('materials') ?? [])
        .map((mm) => mm.agricultural_material_id)
        .filter(Boolean)
        .join(',')
    const baselineMaterialIdsRef = useRef<string | null>(null)
    useEffect(() => {
        if (loadingInitial) return
        if (baselineMaterialIdsRef.current !== null) return
        baselineMaterialIdsRef.current = (initial?.materials ?? [])
            .map((mm) => mm.agricultural_material_id)
            .filter(Boolean)
            .join(',')
    }, [loadingInitial, initial])

    const singleMaterialId =
        needsQuarantine && materialIdsKey && !materialIdsKey.includes(',')
            ? materialIdsKey
            : undefined
    const { data: singleMaterial } = useAgriculturalMaterial(
        singleMaterialId,
        Boolean(singleMaterialId),
    )
    useEffect(() => {
        const baseline = baselineMaterialIdsRef.current
        if (baseline === null) return // chưa arm (đang load nhật ký)
        if (materialIdsKey === baseline) return // chưa đổi so với lần xử lý trước
        if (!needsQuarantine) {
            baselineMaterialIdsRef.current = materialIdsKey
            return
        }
        if (!singleMaterialId) {
            // 0 hoặc >1 vật tư → xóa trắng, hộ tự nhập (giống hệ cũ). Không
            // đoán hộ số nào: cách ly của hỗn hợp phải lấy con số DÀI NHẤT, mà
            // `quarantine_period` là text tự do nên tách số ra là dễ sai.
            form.setValue('quarantine_note', '')
            baselineMaterialIdsRef.current = materialIdsKey
            return
        }
        // Đúng 1 vật tư: đợi fetch xong đúng id rồi điền (chưa cập nhật baseline để
        // effect chạy lại khi data về).
        if (singleMaterial && singleMaterial.id === singleMaterialId) {
            const q = singleMaterial.quarantine_period ?? ''
            form.setValue('quarantine_note', q)
            baselineMaterialIdsRef.current = materialIdsKey
        }
    }, [
        materialIdsKey,
        needsQuarantine,
        singleMaterialId,
        singleMaterial,
        form,
    ])

    // Wizard 3 bước — state đặt ở container (hook này) nên `step` sống sót khi
    // đổi layout desktop⇄mobile lúc resize.
    const wizard = useCareLogWizard({
        onInvalidStep: () => workflow.step('validation_failed'),
        form,
        needsMaterials,
        needsQuantity,
    })

    // Names/thumbs for materials already on the log — the picker uses these to
    // label a material that is off the current search page (GET hydrates them).
    const materialFallbackLabels = Object.fromEntries(
        (initial?.materials ?? []).map((mm) => [
            mm.agricultural_material_id,
            {
                name: mm.material_name ?? '',
                image: mm.material_image_url,
                unit: mm.material_unit,
            },
        ]),
    )

    const { data: growerAreas, isFetching: loadingAreas } =
        useCareLogGrowerAreas(selectedGrowerId || null)
    const parcelOptions = (growerAreas ?? []).map((a) => ({
        value: a.id,
        label: a.code ? `${a.code} — ${a.name}` : a.name,
    }))
    requireParcelsRef.current = shouldRequireParcels(
        loadingAreas,
        parcelOptions.length,
    )
    const prevGrowerRef = useRef(initial?.grower_id ?? '')
    useEffect(() => {
        if (prevGrowerRef.current !== selectedGrowerId) {
            // Đổi HẲN sang hộ khác (không tính lần điền đầu tiên): cả vùng trồng
            // lẫn vật tư đều thuộc về hộ cũ. Danh mục vật tư lọc theo hộ ("vật
            // tư ngoài" chỉ gán cho một số hộ), nên giữ lại lựa chọn cũ là gửi
            // lên server một vật tư không thuộc hộ mới → usecase trả 400
            // ErrMaterialNotForGrower, hộ không hiểu vì sao lưu không được.
            if (prevGrowerRef.current !== '') {
                form.setValue('parcels', [], { shouldValidate: false })
                form.setValue('materials', [], { shouldValidate: false })
            }
            prevGrowerRef.current = selectedGrowerId
        }
    }, [selectedGrowerId, form])

    // Mở từ mã QR: chờ ngữ cảnh QR + danh sách vùng của hộ về đủ rồi mới hiện
    // form (page render "Đang tải…"). Hiện sớm thì người dùng kịp chọn tay rồi
    // bị reset `values` ghi đè, hoặc kịp bấm "Tiếp theo" rồi bị tự bấm thêm lần
    // nữa (quá bước Công việc). Chốt MỘT lần: xong rồi thì quay lại bước Địa
    // điểm đổi hộ (vùng tải lại) cũng không bật lại màn chờ.
    const qrSettledRef = useRef(false)
    const prefillingFromQr =
        !qrSettledRef.current &&
        (loadingQrArea ||
            (!!qrArea &&
                (selectedGrowerId !== qrArea.grower_id || loadingAreas)))
    const advanceWizard = wizard.next
    useEffect(() => {
        if (qrSettledRef.current || prefillingFromQr) return
        qrSettledRef.current = true
        // Bước Địa điểm đã điền sẵn → tự "bấm Tiếp theo" đúng một lần, sau khi
        // danh sách vùng về để luật bắt buộc vùng validate đúng. Không hợp lệ
        // (vd vùng vừa bị xoá) thì wizard giữ bước này và hiện lỗi như bấm tay.
        // Ngữ cảnh 404 hoặc tải vùng lỗi → không điền/không nhảy, tạo như thường.
        if (qrArea && growerAreas !== undefined) void advanceWizard()
    }, [prefillingFromQr, qrArea, growerAreas, advanceWizard])
    // Activities that don't use materials must not keep leftover material rows:
    // the section is hidden but zod still validates the array, so an empty/invalid
    // row would block submit with an error the user can't see. Drop them.
    useEffect(() => {
        if (!needsMaterials && (form.getValues('materials') ?? []).length > 0) {
            form.setValue('materials', [], { shouldValidate: false })
        }
    }, [needsMaterials, form])

    // Đổi hoạt động → xóa vật tư đã chọn (phân bón vs thuốc khác category, tránh
    // lẫn + badge uuid mồ côi). Né bug đè lúc mở sửa: baseline arm từ `initial`
    // sau khi load, chỉ xóa khi NGƯỜI DÙNG đổi hoạt động.
    const prevActivityRef = useRef<string | null>(null)
    useEffect(() => {
        if (loadingInitial) return
        if (prevActivityRef.current === null) {
            prevActivityRef.current =
                initial?.activity_type ?? initialActivity ?? 'fertilizing'
            return
        }
        if (prevActivityRef.current !== activity) {
            prevActivityRef.current = activity
            if ((form.getValues('materials') ?? []).length > 0) {
                form.setValue('materials', [], { shouldValidate: false })
            }
        }
    }, [activity, loadingInitial, initial, initialActivity, form])

    // Evidence previews live here (not in the layout) so they survive the
    // mobile⇄desktop swap. Seeded from the log's existing files once they load.
    const [filePreviews, setFilePreviews] = useState<
        Record<string, FilePreview>
    >({})
    const [uploadingFiles, setUploadingFiles] = useState(false)
    useEffect(() => {
        const files = initial?.files
        if (!files || files.length === 0) return
        setFilePreviews((prev) => {
            let changed = false
            const next = { ...prev }
            for (const f of files) {
                if (!next[f.id]) {
                    next[f.id] = {
                        url: f.url,
                        isVideo: !!f.mime_type?.startsWith('video/'),
                    }
                    changed = true
                }
            }
            return changed ? next : prev
        })
    }, [initial])

    const parcelAreaIds = new Set(parcelOptions.map((o) => o.value))
    const parcelOptionsWithOrphans = [
        ...parcelOptions,
        ...selectedParcels
            .filter((id) => !parcelAreaIds.has(id))
            .map((id) => ({ value: id, label: m.care_log_parcel_removed() })),
    ]

    async function handleSubmit(values: CareLogFormValues) {
        const payload: UpsertCareLogRequest = {
            grower_id: values.grower_id,
            activity_type: values.activity_type,
            log_date: values.log_date,
            // Hái chè: ô chung. Bón phân/Phun thuốc: tổng định lượng các vật tư
            // (giữ số tổng cho các màn xem/báo cáo cũ vẫn đọc được `quantity`).
            quantity: needsQuantity
                ? values.quantity
                : needsMaterials
                  ? values.materials.reduce(
                        (s, mm) => s + (mm.quantity || 0),
                        0,
                    )
                  : 0,
            quarantine_note: needsQuarantine
                ? values.quarantine_note || null
                : null,
            note: values.note || null,
            parcels: values.parcels,
            materials: needsMaterials ? values.materials : [],
            file_ids: values.file_ids,
            implementer_ids: values.implementer_ids,
        }
        try {
            if (mode === 'edit' && careLogId) {
                await workflow.run(
                    () => updateMut.mutateAsync(payload),
                    (res) => res.data?.id,
                )
            } else {
                await workflow.run(
                    () => createMut.mutateAsync(payload),
                    (res) => res.data?.id,
                )
            }
            onSaved()
        } catch {
            // mutation onError surfaces the toast
        }
    }

    function toggleParcel(id: string) {
        workflow.interact('selection')
        const next = selectedParcels.includes(id)
            ? selectedParcels.filter((p) => p !== id)
            : [...selectedParcels, id]
        form.setValue('parcels', next, { shouldValidate: true })
    }

    function handleGrowerChange(growerId: string) {
        workflow.interact('selection')
        form.setValue('grower_id', growerId, {
            shouldValidate: true,
        })
    }

    return {
        mode,
        form,
        onBack: () => {
            workflow.finish('closed')
            onBack()
        },
        loadingInitial,
        prefillingFromQr,
        submitting,
        isAdmin,
        growersLoading,
        households,
        noHousehold,
        initialGrowerName: initial?.grower_name ?? qrArea?.grower_name,
        selectedGrowerId,
        handleGrowerChange,
        selectedImplementerIds,
        implementerOptions,
        activity,
        logDate,
        selectedParcels,
        needsMaterials,
        needsQuantity,
        needsQuarantine,
        quantityLabel,
        quantityHint,
        materialsLabel,
        materialFallbackLabels,
        parcelOptionsWithOrphans,
        requireParcels: requireParcelsRef.current,
        loadingParcels: loadingAreas,
        filePreviews,
        uploadingFiles,
        setUploadingFiles,
        setFilePreviews,
        handleSubmit,
        toggleParcel,
        wizard: {
            ...wizard,
            next: async () => {
                workflow.interact('review')
                await wizard.next()
            },
            back: () => {
                workflow.interact('review')
                wizard.back()
            },
            goTo: (target) => {
                workflow.interact('review')
                wizard.goTo(target)
            },
            onInvalid: (errors) => {
                workflow.step('validation_failed')
                wizard.onInvalid(errors)
            },
        },
    }
}
